// frontend/src/pages/ProcedureMaster.js
import React, { useState, useEffect } from "react";
import { manageDepartments, manageProcedures, supabase } from "../supabase";
import { useOrganization } from "../contexts/OrganizationContext"; // 追加
import "./ProcedureMaster.css";

const roleTypeMap = {
  scrub: "器械出し",
  circulating: "外回り"
};

const REQUIREMENT_LABEL = {
  required: "必須",
  optional: "任意",
};

function ProcedureMaster() {
  const { currentOrganization } = useOrganization(); // 追加
  const [departments, setDepartments] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [levels, setLevels] = useState([]); // 🔥 術式レベルマスタ
  const [newDeptName, setNewDeptName] = useState("");
  const [newProcedure, setNewProcedure] = useState({
    name: "",
    departmentId: "",
    requiredRoles: [],
    levelId: null,  // 🔥 術式のレベル
  });
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [showProcModal, setShowProcModal] = useState(false);
  const [showLevelModal, setShowLevelModal] = useState(false); // 🔥 レベル管理
  const [editingDept, setEditingDept] = useState(null);
  const [editingProc, setEditingProc] = useState(null);
  const [selectedDeptId, setSelectedDeptId] = useState("all");
  const [selectedLevelId, setSelectedLevelId] = useState("all"); // 🔥 レベル絞り込み
  const [loading, setLoading] = useState(false);

  // 初期データ読み込み
  useEffect(() => {
    fetchDepartments();
    fetchProcedures();
    fetchLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id]);

  // 🔥 術式レベル一覧取得
  const fetchLevels = async () => {
    if (!currentOrganization?.id) return;
    try {
      const { data, error } = await supabase
        .from('procedure_levels')
        .select('id, name, description, display_order, color')
        .eq('organization_id', currentOrganization.id)
        .order('display_order');
      if (error) throw error;
      setLevels(data || []);
    } catch (e) {
      console.error('レベル取得失敗:', e);
    }
  };

  // 診療科データ取得
  const fetchDepartments = async () => {
    if (!currentOrganization?.id) return; // 追加
    setLoading(true);
    try {
      const data = await manageDepartments('list', { organizationId: currentOrganization.id });
      setDepartments(data || []);
    } catch (error) {
      console.error("診療科の取得に失敗しました:", error);
      alert("診療科の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  // 術式データ取得
  const fetchProcedures = async () => {
    if (!currentOrganization?.id) return; // 追加
    setLoading(true);
    try {
      const data = await manageProcedures('list', { organizationId: currentOrganization.id });
      // required_rolesをパース
      const parsedData = (data || []).map(proc => ({
        ...proc,
        required_roles: typeof proc.required_roles === 'string' 
          ? JSON.parse(proc.required_roles) 
          : (proc.required_roles || [])
      }));
      setProcedures(parsedData);
    } catch (error) {
      console.error("術式の取得に失敗しました:", error);
      alert("術式の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  // 診療科の追加・更新
  const handleAddOrUpdateDepartment = async () => {
    if (!currentOrganization?.id) return; // 追加
    if (!newDeptName.trim()) {
      alert("診療科名を入力してください");
      return;
    }

    try {
      if (editingDept) {
        await manageDepartments('update', {
          departmentId: editingDept.id,
          name: newDeptName,
          organizationId: currentOrganization.id // 変更
        });
        alert("診療科を更新しました");
      } else {
        await manageDepartments('add', {
          name: newDeptName,
          organizationId: currentOrganization.id // 変更
        });
        alert("診療科を追加しました");
      }

      setNewDeptName("");
      setEditingDept(null);
      setShowDeptModal(false);
      await fetchDepartments();
    } catch (error) {
      console.error("診療科の保存に失敗しました:", error);
      alert("診療科の保存に失敗しました");
    }
  };

  // 診療科の削除
  const handleDeleteDepartment = async (deptId) => {
    if (!window.confirm("本当に削除しますか？この診療科に紐づく術式も削除されます。")) return;
  
    try {
      // Edge Functionの代わりに直接Supabaseを使用
      const { supabase } = await import('../supabase');
      
      // 関連する術式を先に削除
      await supabase
        .from('procedures')
        .delete()
        .eq('department_id', deptId)
        .eq('organization_id', currentOrganization.id);
      
      // 診療科を削除
      const { error } = await supabase
        .from('departments')
        .delete()
        .eq('id', deptId)
        .eq('organization_id', currentOrganization.id);
      
      if (error) throw error;
      
      alert("診療科を削除しました");
      await fetchDepartments();
      await fetchProcedures();
    } catch (error) {
      console.error("診療科の削除に失敗しました:", error);
      alert("診療科の削除に失敗しました");
    }
  };

  // 術式の追加・更新
  const handleAddOrUpdateProcedure = async () => {
    if (!currentOrganization?.id) return; // 追加
    if (!newProcedure.name.trim() || !newProcedure.departmentId) {
      alert("術式名と診療科を選択してください");
      return;
    }

    try {
      let savedProcedureId = editingProc?.id;
      if (editingProc) {
        await manageProcedures('update', {
          procedureId: editingProc.id,
          name: newProcedure.name,
          departmentId: newProcedure.departmentId,
          requiredRoles: newProcedure.requiredRoles,
          organizationId: currentOrganization.id // 変更
        });
        alert("術式を更新しました");
      } else {
        const added = await manageProcedures('add', {
          name: newProcedure.name,
          departmentId: newProcedure.departmentId,
          requiredRoles: newProcedure.requiredRoles,
          organizationId: currentOrganization.id // 変更
        });
        savedProcedureId = added?.id || added?.[0]?.id || null;
        alert("術式を追加しました");
      }

      // 🔥 level_id の更新は別途 supabase で直接 (Edge Function は level_id 未対応)
      if (savedProcedureId) {
        await supabase
          .from('procedures')
          .update({ level_id: newProcedure.levelId || null })
          .eq('id', savedProcedureId);
      }

      setNewProcedure({ name: "", departmentId: "", requiredRoles: [], levelId: null });
      setEditingProc(null);
      setShowProcModal(false);
      await fetchProcedures();
    } catch (error) {
      console.error("術式の保存に失敗しました:", error);
      alert("術式の保存に失敗しました");
    }
  };

  // 術式の削除
  const handleDeleteProcedure = async (procId) => {
    if (!currentOrganization?.id) return; // 追加
    if (!window.confirm("本当に削除しますか？")) return;

    try {
      await manageProcedures('delete', {
        procedureId: procId,
        organizationId: currentOrganization.id // 変更
      });
      alert("術式を削除しました");
      await fetchProcedures();
    } catch (error) {
      console.error("術式の削除に失敗しました:", error);
      alert("術式の削除に失敗しました");
    }
  };

  // 必須役割の追加
  const addRequiredRole = () => {
    setNewProcedure(prev => ({
      ...prev,
      requiredRoles: [...prev.requiredRoles, {
        type: "scrub",
        count: 1,
        label: "",
        levelId: null,            // この役割のレベル基準
        requirement: "required",  // 必須区分: required / optional
      }],
    }));
  };

  // 必須役割の更新
  const updateRequiredRole = (idx, key, value) => {
    const updated = [...newProcedure.requiredRoles];
    updated[idx][key] = value;
    setNewProcedure(prev => ({ ...prev, requiredRoles: updated }));
  };

  // 必須役割の削除
  const removeRequiredRole = (idx) => {
    const updated = [...newProcedure.requiredRoles];
    updated.splice(idx, 1);
    setNewProcedure(prev => ({ ...prev, requiredRoles: updated }));
  };

  // フィルタリングされた術式 (診療科 + レベルで絞り込み)
  const filteredProcedures = procedures.filter((proc) => {
    if (selectedDeptId !== "all" && proc.department_id !== selectedDeptId) return false;
    if (selectedLevelId === "all") return true;
    if (selectedLevelId === "none") return !proc.level_id;
    return proc.level_id === selectedLevelId;
  });

  // レベル名取得ヘルパー
  const getLevelInfo = (levelId) => levels.find((lv) => lv.id === levelId);

  // 組織が選択されていない場合
  if (!currentOrganization?.id) {
    return (
      <div className="procedure-master-container">
        <div style={{ textAlign: "center", padding: "3rem", color: "#666" }}>
          組織を選択してください
        </div>
      </div>
    );
  }

  return (
    <div className="procedure-master-container">
      {loading && <div className="loading-overlay">読み込み中...</div>}

      {/* 診療科セクション */}
      <section className="master-section">
        <div className="section-header">
          <h2>📋 診療科マスター</h2>
          <button 
            className="add-btn primary" 
            onClick={() => setShowDeptModal(true)}
          >
            ＋ 診療科を追加
          </button>
        </div>

        <div className="cards-grid">
          {departments.map((dept) => (
            <div key={dept.id} className="master-card">
              <div className="card-content">
                <h3 className="card-title">{dept.name}</h3>
                <div className="card-stats">
                  {procedures.filter(p => p.department_id === dept.id).length}件の術式
                </div>
              </div>
              <div className="card-actions">
                <button
                  className="btn-edit"
                  onClick={() => {
                    setEditingDept(dept);
                    setNewDeptName(dept.name);
                    setShowDeptModal(true);
                  }}
                >
                  編集
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteDepartment(dept.id)}
                >
                  削除
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 術式セクション */}
      <section className="master-section">
        <div className="section-header">
          <h2>🛠 術式マスター</h2>
          <div className="header-controls">
            <select
              className="dept-filter"
              value={selectedDeptId}
              onChange={(e) => setSelectedDeptId(e.target.value)}
            >
              <option value="all">すべての診療科</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
            {/* 🔥 レベルフィルタ */}
            <select
              className="dept-filter"
              value={selectedLevelId}
              onChange={(e) => setSelectedLevelId(e.target.value)}
              style={{ marginLeft: '8px' }}
            >
              <option value="all">すべてのレベル</option>
              <option value="none">(未分類)</option>
              {levels.map((lv) => (
                <option key={lv.id} value={lv.id}>{lv.name}</option>
              ))}
            </select>
            <button
              className="add-btn"
              onClick={() => setShowLevelModal(true)}
              style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', marginRight: 6 }}
            >
              🎚 レベル管理
            </button>
            <button
              className="add-btn primary"
              onClick={() => setShowProcModal(true)}
            >
              ＋ 術式を追加
            </button>
          </div>
        </div>

        <div className="table-container">
          <table className="modern-table">
            <thead>
              <tr>
                <th>術式名</th>
                <th>診療科</th>
                <th>レベル</th>
                <th>必須役割</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredProcedures.map((proc) => {
                const levelInfo = getLevelInfo(proc.level_id);
                return (
                <tr key={proc.id}>
                  <td className="proc-name">{proc.name}</td>
                  <td>
                    <span className="dept-badge">
                      {departments.find((d) => d.id === proc.department_id)?.name || "不明"}
                    </span>
                  </td>
                  <td>
                    {levelInfo ? (
                      <span style={{
                        padding: '3px 10px', borderRadius: '12px',
                        fontSize: '11px', fontWeight: 700,
                        background: `${levelInfo.color || '#3b82f6'}22`,
                        color: levelInfo.color || '#3b82f6',
                        border: `1px solid ${levelInfo.color || '#3b82f6'}66`,
                      }}>{levelInfo.name}</span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '11px' }}>未分類</span>
                    )}
                  </td>
                  <td>
  {proc.required_roles && proc.required_roles.length > 0 ? (
    <div className="roles-list" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {proc.required_roles.map((role, idx) => {
        const roleName = role.label ? `${roleTypeMap[role.type] || role.type}(${role.label})` : (roleTypeMap[role.type] || role.type);
        const roleLevel = getLevelInfo(role.levelId);
        const req = role.requirement || "required";
        return (
          <div key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <span className="role-tag">{roleName}</span>
            <span style={{
              padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700,
              background: req === 'required' ? '#fef2f2' : '#f1f5f9',
              color: req === 'required' ? '#b91c1c' : '#475569',
              border: '1px solid ' + (req === 'required' ? '#fecaca' : '#cbd5e1'),
            }}>{REQUIREMENT_LABEL[req]}</span>
            {roleLevel ? (
              <span style={{
                padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                background: `${roleLevel.color || '#3b82f6'}22`,
                color: roleLevel.color || '#3b82f6',
                border: `1px solid ${roleLevel.color || '#3b82f6'}66`,
              }}>{roleLevel.name}</span>
            ) : (
              <span style={{ fontSize: 10, color: '#94a3b8' }}>レベル未設定</span>
            )}
          </div>
        );
      })}
    </div>
  ) : (
    <span className="text-muted">設定なし</span>
  )}
</td>
                  <td className="actions-cell">
                    <button
                      className="btn-edit"
                      onClick={() => {
                        setEditingProc(proc);
                        setNewProcedure({
                          name: proc.name,
                          departmentId: proc.department_id,
                          requiredRoles: (proc.required_roles || []).map((r) => ({
                            ...r,
                            levelId: r.levelId ?? null,
                            requirement: r.requirement ?? "required",
                          })),
                          levelId: proc.level_id || null,
                        });
                        setShowProcModal(true);
                      }}
                    >
                      編集
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteProcedure(proc.id)}
                    >
                      削除
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* 診療科モーダル */}
      {showDeptModal && (
        <div className="modal-backdrop" onClick={() => {
          setShowDeptModal(false);
          setEditingDept(null);
          setNewDeptName("");
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingDept ? "診療科を編集" : "診療科を追加"}</h3>
              <button 
                className="close-btn" 
                onClick={() => {
                  setShowDeptModal(false);
                  setEditingDept(null);
                  setNewDeptName("");
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>診療科名</label>
                <input
                  type="text"
                  placeholder="例: 心臓血管外科"
                  value={newDeptName}
                  onChange={(e) => setNewDeptName(e.target.value)}
                  className="input-field"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => {
                  setShowDeptModal(false);
                  setEditingDept(null);
                  setNewDeptName("");
                }}
              >
                キャンセル
              </button>
              <button 
                className="btn-primary" 
                onClick={handleAddOrUpdateDepartment}
              >
                {editingDept ? "更新" : "追加"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 術式モーダル */}
      {showProcModal && (
        <div className="modal-backdrop" onClick={() => {
          setShowProcModal(false);
          setEditingProc(null);
          setNewProcedure({ name: "", departmentId: "", requiredRoles: [] });
        }}>
          <div className="modal large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingProc ? "術式を編集" : "術式を追加"}</h3>
              <button 
                className="close-btn" 
                onClick={() => {
                  setShowProcModal(false);
                  setEditingProc(null);
                  setNewProcedure({ name: "", departmentId: "", requiredRoles: [] });
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>術式名</label>
                <input
                  type="text"
                  placeholder="例: 冠動脈バイパス術"
                  value={newProcedure.name}
                  onChange={(e) =>
                    setNewProcedure({ ...newProcedure, name: e.target.value })
                  }
                  className="input-field"
                />
              </div>

              <div className="form-group">
                <label>診療科</label>
                <select
                  value={newProcedure.departmentId}
                  onChange={(e) =>
                    setNewProcedure({
                      ...newProcedure,
                      departmentId: e.target.value
                    })
                  }
                  className="input-field"
                >
                  <option value="">選択してください</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 🔥 術式レベル選択 */}
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>レベル (難易度)</label>
                  <button type="button" onClick={() => setShowLevelModal(true)}
                    style={{
                      fontSize: '11px', padding: '4px 10px',
                      background: '#eff6ff', color: '#1d4ed8',
                      border: '1px solid #bfdbfe', borderRadius: '6px',
                      cursor: 'pointer',
                    }}>
                    レベル管理
                  </button>
                </div>
                <select
                  value={newProcedure.levelId || ''}
                  onChange={(e) => setNewProcedure({
                    ...newProcedure,
                    levelId: e.target.value || null,
                  })}
                  className="input-field"
                >
                  <option value="">(未分類)</option>
                  {levels.map((lv) => (
                    <option key={lv.id} value={lv.id}>{lv.name}</option>
                  ))}
                </select>
                {levels.length === 0 && (
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: 4 }}>
                    まだレベルが定義されていません。「レベル管理」から追加してください。
                  </div>
                )}
              </div>

              <div className="form-group">
                <div className="form-group-header">
                  <label>必須役割</label>
                  <button 
                    type="button" 
                    onClick={addRequiredRole} 
                    className="btn-add-role"
                  >
                    ＋ 役割を追加
                  </button>
                </div>
                
                {newProcedure.requiredRoles.map((role, idx) => (
                  <div key={idx} style={{
                    border: '1.5px solid #e2e8f0',
                    borderRadius: 8,
                    padding: '10px 12px',
                    marginBottom: 10,
                    background: '#f8fafc',
                  }}>
                    <div className="role-row" style={{ marginBottom: 8 }}>
                      <select
                        value={role.type}
                        onChange={(e) => updateRequiredRole(idx, "type", e.target.value)}
                        className="role-type-select"
                      >
                        <option value="scrub">器械出し</option>
                        <option value="circulating">外回り</option>
                      </select>
                      <input
                        type="text"
                        placeholder="ラベル (例: 消化器側)"
                        value={role.label || ""}
                        onChange={(e) => updateRequiredRole(idx, "label", e.target.value)}
                        className="role-label-input"
                      />
                      <button
                        type="button"
                        onClick={() => removeRequiredRole(idx)}
                        className="btn-remove-role"
                      >
                        削除
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 180px', minWidth: 160 }}>
                        <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>
                          レベル基準
                        </label>
                        <select
                          value={role.levelId || ""}
                          onChange={(e) => updateRequiredRole(idx, "levelId", e.target.value || null)}
                          style={{ width: '100%', padding: '6px 8px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                        >
                          <option value="">(未設定)</option>
                          {levels.map((lv) => (
                            <option key={lv.id} value={lv.id}>{lv.name}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ flex: '0 0 140px' }}>
                        <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>
                          必須区分
                        </label>
                        <select
                          value={role.requirement || "required"}
                          onChange={(e) => updateRequiredRole(idx, "requirement", e.target.value)}
                          style={{
                            width: '100%', padding: '6px 8px', borderRadius: 6, fontSize: 13,
                            border: '1.5px solid ' + ((role.requirement || 'required') === 'required' ? '#ef4444' : '#94a3b8'),
                            background: (role.requirement || 'required') === 'required' ? '#fef2f2' : '#f1f5f9',
                            fontWeight: 600,
                            color: (role.requirement || 'required') === 'required' ? '#b91c1c' : '#475569',
                          }}
                        >
                          <option value="required">必須</option>
                          <option value="optional">任意</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => {
                  setShowProcModal(false);
                  setEditingProc(null);
                  setNewProcedure({ name: "", departmentId: "", requiredRoles: [] });
                }}
              >
                キャンセル
              </button>
              <button 
                className="btn-primary" 
                onClick={handleAddOrUpdateProcedure}
              >
                {editingProc ? "更新" : "追加"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔥 レベル管理モーダル */}
      {showLevelModal && (
        <LevelManagementModal
          levels={levels}
          organizationId={currentOrganization.id}
          onClose={() => setShowLevelModal(false)}
          onChanged={fetchLevels}
        />
      )}
    </div>
  );
}

// 🔥 レベルマスタ管理モーダル
const LevelManagementModal = ({ levels, organizationId, onClose, onChanged }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', color: '', display_order: 0 });
  const [saving, setSaving] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!name.trim()) return alert('レベル名は必須');
    setSaving(true);
    try {
      const nextOrder = (levels.length > 0
        ? Math.max(...levels.map((lv) => lv.display_order || 0)) + 1
        : 0);
      const { error } = await supabase
        .from('procedure_levels')
        .insert({
          organization_id: organizationId,
          name: name.trim(),
          description: description.trim() || null,
          color,
          display_order: nextOrder,
        });
      if (error) throw error;
      setName(''); setDescription(''); setColor('#3b82f6');
      await onChanged();
    } catch (e) {
      alert(`追加エラー: ${e.message}`);
    } finally { setSaving(false); }
  };

  const startEdit = (lv) => {
    setEditingId(lv.id);
    setEditForm({
      name: lv.name, description: lv.description || '',
      color: lv.color || '#3b82f6', display_order: lv.display_order || 0,
    });
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('procedure_levels')
        .update({
          name: editForm.name.trim(),
          description: editForm.description.trim() || null,
          color: editForm.color,
          display_order: Number(editForm.display_order) || 0,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingId);
      if (error) throw error;
      setEditingId(null);
      await onChanged();
    } catch (e) {
      alert(`更新エラー: ${e.message}`);
    } finally { setSaving(false); }
  };

  const handleDelete = async (lv) => {
    if (!window.confirm(`「${lv.name}」を削除しますか? (このレベルが設定された術式は「未分類」になります)`)) return;
    try {
      const { error } = await supabase
        .from('procedure_levels')
        .delete()
        .eq('id', lv.id);
      if (error) throw error;
      await onChanged();
    } catch (e) {
      alert(`削除エラー: ${e.message}`);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <h3>🎚 術式レベル管理</h3>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b',
          }}>×</button>
        </div>
        <div className="modal-body">
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
          術式を「新人向け」「中堅向け」「上級者向け」等にレベル分類するためのマスタです。
          名前・色・並び順は組織ごとに自由に設定できます。
        </p>

        {/* 新規追加フォーム */}
        <form onSubmit={handleAdd} style={{
          display: 'grid', gridTemplateColumns: '2fr 3fr 60px auto',
          gap: 8, alignItems: 'end', marginTop: 12, marginBottom: 16,
        }}>
          <label>
            <div style={{ fontSize: 11, color: '#64748b' }}>レベル名 *</div>
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="例: ★1 / 初級 / 1年目"
              required className="input-field" />
          </label>
          <label>
            <div style={{ fontSize: 11, color: '#64748b' }}>説明 (任意)</div>
            <input value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="例: 新人が独り立ちすべきレベル"
              className="input-field" />
          </label>
          <label>
            <div style={{ fontSize: 11, color: '#64748b' }}>色</div>
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
              style={{ width: '100%', height: 36, border: '1px solid #d1d5db', borderRadius: 6 }} />
          </label>
          <button type="submit" disabled={saving} style={{
            padding: '8px 16px',
            background: saving ? '#94a3b8' : '#059669',
            color: 'white', border: 'none', borderRadius: 6,
            fontWeight: 700, fontSize: 13, height: 36,
            cursor: saving ? 'not-allowed' : 'pointer',
          }}>追加</button>
        </form>

        {/* 一覧 */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: '#f8fafc' }}>
            <tr>
              <th style={{ padding: 8, textAlign: 'left', width: 80 }}>並び順</th>
              <th style={{ padding: 8, textAlign: 'left' }}>レベル</th>
              <th style={{ padding: 8, textAlign: 'left' }}>説明</th>
              <th style={{ padding: 8, textAlign: 'left', width: 50 }}>色</th>
              <th style={{ padding: 8, width: 140 }}></th>
            </tr>
          </thead>
          <tbody>
            {levels.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>
                まだレベルが定義されていません
              </td></tr>
            )}
            {levels.map((lv) => {
              const isEdit = editingId === lv.id;
              return (
                <tr key={lv.id} style={{ borderBottom: '1px solid #e5e7eb', background: isEdit ? '#fefce8' : 'white' }}>
                  <td style={{ padding: 8 }}>
                    {isEdit ? (
                      <input type="number" value={editForm.display_order}
                        onChange={(e) => setEditForm({ ...editForm, display_order: e.target.value })}
                        style={{ width: 60, padding: 4 }} />
                    ) : lv.display_order}
                  </td>
                  <td style={{ padding: 8, fontWeight: 600 }}>
                    {isEdit ? (
                      <input value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="input-field" />
                    ) : (
                      <span style={{
                        padding: '3px 10px', borderRadius: '12px',
                        fontSize: '11px', fontWeight: 700,
                        background: `${lv.color || '#3b82f6'}22`,
                        color: lv.color || '#3b82f6',
                        border: `1px solid ${lv.color || '#3b82f6'}66`,
                      }}>{lv.name}</span>
                    )}
                  </td>
                  <td style={{ padding: 8, color: '#64748b' }}>
                    {isEdit ? (
                      <input value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="input-field" />
                    ) : (lv.description || '-')}
                  </td>
                  <td style={{ padding: 8 }}>
                    {isEdit ? (
                      <input type="color" value={editForm.color}
                        onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                        style={{ width: 40, height: 28 }} />
                    ) : (
                      <div style={{ width: 24, height: 24, borderRadius: 6, background: lv.color || '#3b82f6' }} />
                    )}
                  </td>
                  <td style={{ padding: 8, textAlign: 'right' }}>
                    {isEdit ? (
                      <>
                        <button onClick={handleUpdate} disabled={saving} style={{
                          padding: '4px 10px', background: '#2563eb', color: 'white',
                          border: 'none', borderRadius: 6, fontSize: 11, marginRight: 4,
                          cursor: 'pointer',
                        }}>保存</button>
                        <button onClick={() => setEditingId(null)} style={{
                          padding: '4px 10px', background: '#e5e7eb',
                          border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                        }}>取消</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(lv)} style={{
                          padding: '4px 10px', background: '#eff6ff', color: '#1d4ed8',
                          border: 'none', borderRadius: 6, fontSize: 11, marginRight: 4,
                          cursor: 'pointer',
                        }}>編集</button>
                        <button onClick={() => handleDelete(lv)} style={{
                          padding: '4px 10px', background: '#fee2e2', color: '#dc2626',
                          border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                        }}>削除</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        </div>
        <div className="modal-footer">
          <button onClick={onClose} style={{
            padding: '8px 16px', background: '#e5e7eb',
            border: 'none', borderRadius: 6, cursor: 'pointer',
          }}>閉じる</button>
        </div>
      </div>
    </div>
  );
};

export default ProcedureMaster;